// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, Outlet } from 'react-router-dom';
import './index.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons';
import Login from './pages/Login';
import Layout from './components/Layout';
import KnowledgeBase from './pages/KnowledgeBase';
import UploadForm from './pages/UploadForm';
import DocumentUpload from './pages/DocumentUpload';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Prompts from './pages/Prompts';
import Integrations from './pages/Integrations';
import ChatInterface from './components/ChatInterface';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import Profile from './pages/Profile';
import CalendarSettings from './pages/CalendarSettings';
import CrawlDetails from './pages/CrawlDetails';
import CitationAnalyticsPage from './pages/CitationAnalyticsPage';
import CitationsPage from './pages/CitationsPage';
import TokenUsageAnalytics from './pages/TokenUsageAnalytics';
import UnifiedAnalytics from './pages/UnifiedAnalytics';
import ChatHistory from './pages/ChatHistory';
import SettingsPage from './pages/SettingsPage';
import RoleBasedRoute from './components/RoleBasedRoute';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import CrawlProgressIndicator from './components/CrawlProgressIndicator';
import CrawlProgressPage from './components/CrawlProgress/CrawlProgressPage';

// Public pages
import OnboardingDemo from './pages/public/OnboardingDemo';
import StandalonePreview from './pages/public/StandalonePreview';
import MarketingLayout from './layouts/MarketingLayout';
import LandingPage from './pages/public/LandingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import PricingPage from './pages/public/PricingPage';

// Consultant pages
import ConsultantDashboard from './pages/consultant/ConsultantDashboard';
import RoleManagement from './pages/consultant/RoleManagement';
import ClientForm from './pages/consultant/ClientForm';
import ConsultantsList from './pages/consultant/ConsultantsList';
import ConsultantForm from './pages/consultant/ConsultantForm';
import ConsultantDetails from './pages/consultant/ConsultantDetails';
import PipelineVisualizer from './pages/consultant/PipelineVisualizer';
import SEOCenter from './pages/consultant/SEOCenter';
import SEODashboardModern from './pages/consultant/SEODashboardModern';
import ChatbotTraining from './pages/ChatbotTraining';
// import CrawlProgressView from './pages/consultant/CrawlProgressView'; // Replaced with CrawlProgressPage
import SupportFlowEditor from './pages/consultant/SupportFlowEditor';
// import BillingDashboard from './pages/consultant/BillingDashboard';  // Commented out - using client billing dashboard
// import BillingDashboardSimple from './pages/consultant/BillingDashboardSimple';  // Removed - billing only for clients
import PlansPage from './pages/consultant/PlansPage';
import AgentsManagement from './pages/consultant/AgentsManagement';
import AgentDetail from './pages/consultant/AgentDetail';

// Client pages
import ClientBillingDashboard from './pages/client/BillingDashboard';
// import UsageDashboard from './pages/client/UsageDashboard'; // Moved to BillingDashboard Usage tab

// Super user pages
import PlansAndPayments from './pages/super/PlansAndPayments';

import ThemeEditor from './components/DynamicTheming/ThemeEditorNew';
import PaymentRedirect from './components/PaymentRedirect';

// Only add icons needed for non-chat functionality
library.add(faMagicWandSparkles);

// Import debug utilities in development
if (process.env.NODE_ENV === 'development') {
    import('./utils/debug');
}

function App() {
    const [showChat, setShowChat] = useState(false);
    const [clientId, setClientId] = useState<string | null>(localStorage.getItem('client_id'));
    const [userRole, setUserRole] = useState<string | null>(localStorage.getItem('user_role'));

    // Monitor localStorage changes
    useEffect(() => {
        const handleStorageChange = () => {
            const storedClientId = localStorage.getItem('client_id');
            const storedRole = localStorage.getItem('user_role');
            
            console.log('Storage changed:', { storedClientId, storedRole });
            
            if (storedClientId !== clientId) {
                setClientId(storedClientId);
            }
            if (storedRole !== userRole) {
                setUserRole(storedRole);
            }
        };

        // Add storage event listener
        window.addEventListener('storage', handleStorageChange);
        
        // Initial check
        handleStorageChange();

        return () => {
            window.removeEventListener('storage', handleStorageChange); 
        };
    }, [clientId, userRole]);

    // Ensure client ID is set before rendering Dashboard
    useEffect(() => {
        const intervalId = setInterval(() => {
            const storedClientId = localStorage.getItem('client_id');
            if (storedClientId !== clientId) {
                setClientId(storedClientId);
            }
        }, 100);

        return () => clearInterval(intervalId);
    }, [clientId]);

    // Create layout element with clientId
    const LayoutWithAuth = () => (
        <ProtectedRoute>
            <Layout onShowChat={() => setShowChat(true)} clientId={clientId}>
                <Outlet />
            </Layout>
        </ProtectedRoute>
    );

    // Debug logging
    console.log('App render:', { clientId, userRole });

    return (
        <ImpersonationProvider>
            <div className="min-h-screen">
                <ToastContainer 
                    position="top-right"
                    style={{ zIndex: 99999 }}
                />
                
                <Routes>
                    {/* Public Routes - No Authentication Required */}
                    
                    {/* Public Auth pages with shared header (marketing links hidden on auth) */}
                    <Route element={<MarketingLayout />}>
                        {/* Temporarily hide marketing pages */}
                        {/* <Route path="/home" element={<LandingPage />} /> */}
                        {/* <Route path="/features" element={<FeaturesPage />} /> */}
                        {/* <Route path="/pricing" element={<PricingPage />} /> */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                    </Route>

                    {/* Demo page - make private (requires auth), no marketing header */}
                    <Route
                        path="/demo"
                        element={
                            <ProtectedRoute>
                                <OnboardingDemo />
                            </ProtectedRoute>
                        }
                    />

                    {/* Standalone preview page - public for consultant sharing */}
                    <Route
                        path="/preview/:clientId"
                        element={<StandalonePreview />}
                    />

                    {/* Root redirect - check if authenticated */}
                    <Route path="/" element={
                        localStorage.getItem('auth_token') ? 
                            <Navigate to={userRole === 'consultant' ? 
                                "/consultant/dashboard" : 
                                (userRole === 'super' ? 
                                "/consultant/dashboard" : "/dashboard")} replace 
                            /> :
                            <Navigate to="/login" replace />
                    } />
                    
                    {/* Protected Routes */}
                    <Route element={<LayoutWithAuth />}>

                        {/* Consultant and Super shared Routes */}
                        <Route element={<RoleBasedRoute allowedRoles={['consultant', 'super']} />}>
                            <Route path="consultant" element={<Navigate to="/consultant/dashboard" replace />} />
                            <Route path="consultant/dashboard" element={<ConsultantDashboard />} />
                            <Route path="consultant/client-form" element={<ClientForm />} />
                            <Route path="consultant/qa-validation" element={<Navigate to="/chatbot-training#qa-validation" replace />} />
                            {/* Redirect old consultant routes to new unified page */}
                            <Route path="consultant/learned-qa/:clientId" element={<Navigate to="/chatbot-training#chat-history" replace />} />
                            <Route path="consultant/fine-tuning/:clientId" element={<Navigate to="/chatbot-training#training" replace />} />
                            <Route path="consultant/seo-center" element={<SEOCenter />} />
                            <Route path="consultant/support-flows" element={<SupportFlowEditor />} />
                            <Route path="consultant/plans" element={<PlansPage />} />
                            <Route path="consultant/agents" element={<AgentsManagement />} />
                            <Route path="consultant/agents/:id" element={<AgentDetail />} />
                            {/* Redirects from old routes */}
                            <Route path="consultant/seo-dashboard" element={<Navigate to="/consultant/seo-center" replace />} />
                            <Route path="consultant/seo-analysis" element={<Navigate to="/consultant/seo-center" replace />} />
                            {/* Use unified CrawlDetails component for both routes */}
                            <Route path="consultant/crawl-progress/:clientId" element={<CrawlDetails />} />
                        </Route>

                        {/* Super-only Routes */}
                        <Route element={<RoleBasedRoute allowedRoles={['super']} />}>
                            <Route path="consultant/role-management" element={<RoleManagement />} />
                            <Route path="consultant/consultants" element={<ConsultantsList />} />
                            <Route path="consultant/plan-management" element={<PlansAndPayments />} />
                            <Route path="consultant/pipeline" element={<PipelineVisualizer />} />
                            <Route path="consultant/consultants/new" element={<ConsultantForm />} />
                            <Route path="consultant/consultants/:id" element={<ConsultantDetails />} />
                        </Route>

                        {/* Shared Routes - Accessible by consultants, super, and clients */}
                        <Route element={<RoleBasedRoute allowedRoles={['consultant', 'super', 'client', 'client_admin']} />}>
                            <Route path="crawl-details/:clientId" element={<CrawlDetails />} />
                        </Route>

                        {/* Client Routes (including profile and settings) */}
                        <Route element={<RoleBasedRoute allowedRoles={['client', 'client_admin']} />}>
                            <Route path="dashboard" element={<UnifiedAnalytics clientId={clientId} />} />
                            <Route path="chatbot-training" element={<ChatbotTraining />} />
                            <Route path="knowledge-base" element={<KnowledgeBase clientId={clientId} />} />
                            <Route path="upload" element={<UploadForm clientId={clientId} />} />
                            <Route path="re-upload" element={<DocumentUpload clientId={clientId} />} />
                            <Route path="integrations" element={<Integrations clientId={clientId} />} />
                            <Route path="calendar-settings" element={<CalendarSettings />} />
                            {/* Redirect old routes to new unified page with appropriate hash */}
                            <Route path="chat-history" element={<Navigate to="/chatbot-training#chat-history" replace />} />
                            <Route path="prompts" element={<Navigate to="/chatbot-training#prompts" replace />} />
                            <Route path="learned-qa" element={<Navigate to="/chatbot-training#chat-history" replace />} />
                            <Route path="fine-tuning" element={<Navigate to="/chatbot-training#training" replace />} />
                            <Route path="citation-analytics" element={<CitationAnalyticsPage />} />
                            <Route path="token-usage-analytics" element={<TokenUsageAnalytics />} />
                            <Route path="citations" element={<CitationsPage />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="theme-editor" element={<ThemeEditor clientId={clientId} />} />
                            <Route path="client/billing" element={<ClientBillingDashboard />} />
                            {/* Usage moved to BillingDashboard Usage tab - access via /client/billing?tab=usage */}
                            {/* Redirect old payment route to new billing payments tab */}
                            <Route path="client/payments" element={<PaymentRedirect />} />
                        </Route>
                    </Route>
                </Routes>
                
                {showChat && (
                    <ChatInterface 
                        clientId={clientId} 
                        onClose={() => setShowChat(false)} 
                    />
                )}
                
                {/* Global crawl progress indicator */}
                <CrawlProgressIndicator />
            </div>
        </ImpersonationProvider>
    );
}


export default App;
